import React, { useEffect, useState, useMemo } from "react";
import PetsIcon from "@mui/icons-material/Pets";
import {
	Box,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	Chip,
	useTheme,
	ListSubheader,
	Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import GroupIcon from "@mui/icons-material/Group";
import FilterListIcon from "@mui/icons-material/FilterList";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { useTranslation } from "react-i18next";

// Group type prefixes for internal value handling
const GROUP_PREFIX = "group:";
const BREEDING_PREFIX = "breeding:";

/**
 * Clean a breeding group name for display by stripping internal prefixes.
 * Raw Firestore data may contain "breeding " prefix (e.g. "breeding 12 Abferkel 3").
 * @param {string} name - Raw breeding group name
 * @returns {string} - Cleaned display name
 */
function cleanBreedingGroupName(name) {
	if (!name) return name;
	// Strip leading "breeding " prefix (case-insensitive)
	return name.replace(/^breeding\s+/i, "").trim();
}

/**
 * Helper function to check if a value matches an animal's group or breeding group
 * @param {Object} animal - Animal object
 * @param {string} selectedValue - Selected group value (with prefix)
 * @returns {boolean} - Whether the animal matches the selected group
 */
export function matchesGroupFilter(animal, selectedValue) {
	if (!animal || !selectedValue) return false;
	if (selectedValue === "all") return true;
	if (selectedValue === "noGroup") {
		return !animal.Group && !animal.BreedingGroup;
	}
	if (selectedValue.startsWith(BREEDING_PREFIX)) {
		const groupName = selectedValue.slice(BREEDING_PREFIX.length);
		return animal.BreedingGroup === groupName;
	}
	if (selectedValue.startsWith(GROUP_PREFIX)) {
		const groupName = selectedValue.slice(GROUP_PREFIX.length);
		return animal.Group === groupName;
	}
	// Fallback for legacy values without prefix (treat as barn group)
	return animal.Group === selectedValue;
}

/**
 * GroupSelector Component
 *
 * @param {Object} props
 * @param {Array} props.animals - Array of animal objects to extract groups from
 * @param {string} props.selectedGroup - Currently selected group
 * @param {Function} props.onGroupChange - Callback when group selection changes
 * @param {boolean} [props.showCounts=true] - Whether to show animal counts per group
 * @param {boolean} [props.persistToSession=true] - Whether to persist selection to sessionStorage
 * @param {string} [props.sessionKey='selectedGroup'] - Key for sessionStorage
 * @param {number|string} [props.minWidth=200] - Minimum width of the selector
 * @param {string} [props.textColor] - Override text color (e.g., 'white' for hero sections)
 * @param {boolean} [props.fullWidth=false] - Whether the selector should expand to 100% width
 * @param {Array<string>} [props.suggestedGroups=[]] - Array of group names that are suggested
*/
export default function GroupSelector({
        animals = [],
        selectedGroup: controlledSelectedGroup,
        onGroupChange,
        showCounts = true,
        persistToSession = true,
        sessionKey = "selectedGroup",
        minWidth = 200,
        textColor,
        fullWidth = false,
        suggestedGroups = [],
}) {
	const { t } = useTranslation();
	const theme = useTheme();

	// Use internal state if not controlled
	const [internalSelectedGroup, setInternalSelectedGroup] = useState("all");
	const selectedGroup =
		controlledSelectedGroup !== undefined
			? controlledSelectedGroup
			: internalSelectedGroup;

	// Store groups as objects with type and name
	const [barnGroups, setBarnGroups] = useState([]);
	const [breedingGroups, setBreedingGroups] = useState([]);
	const [hasNoGroupAnimals, setHasNoGroupAnimals] = useState(false);

	// Extract unique groups from animals (both Group and BreedingGroup)
	useEffect(() => {
		if (animals && animals.length > 0) {
			const uniqueBarnGroups = new Set();
			const uniqueBreedingGroups = new Set();
			let hasNoGroup = false;

			// Extract all unique groups (filter out empty/whitespace-only values)
			animals.forEach((animal) => {
				if (animal.Group && animal.Group.trim()) {
					uniqueBarnGroups.add(animal.Group);
				}
				if (animal.BreedingGroup && animal.BreedingGroup.trim()) {
					uniqueBreedingGroups.add(animal.BreedingGroup);
				}
				if ((!animal.Group || !animal.Group.trim()) && (!animal.BreedingGroup || !animal.BreedingGroup.trim())) {
					hasNoGroup = true;
				}
			});

			// Convert to sorted arrays
			setBarnGroups(Array.from(uniqueBarnGroups).sort());
			setBreedingGroups(Array.from(uniqueBreedingGroups).sort());
			setHasNoGroupAnimals(hasNoGroup);

			// Build list of all valid values for session restore
			const allValues = ["all", "noGroup"];
			uniqueBarnGroups.forEach((g) => allValues.push(GROUP_PREFIX + g));
			uniqueBreedingGroups.forEach((g) => allValues.push(BREEDING_PREFIX + g));

			// Restore selectedGroup from sessionStorage if available and using internal state
			if (persistToSession && controlledSelectedGroup === undefined) {
				const savedGroup = sessionStorage.getItem(sessionKey);
				if (savedGroup && allValues.includes(savedGroup)) {
					setInternalSelectedGroup(savedGroup);
				}
			}
		}
	}, [animals, persistToSession, sessionKey, controlledSelectedGroup]);

	// Save selected group to session storage
	useEffect(() => {
		if (persistToSession) {
			if (selectedGroup === "all") {
				sessionStorage.removeItem(sessionKey);
			} else {
				sessionStorage.setItem(sessionKey, selectedGroup);
			}
		}
	}, [selectedGroup, persistToSession, sessionKey]);

	// Calculate counts for each group
	const groupCounts = useMemo(() => {
		const counts = {
			all: animals.length,
			noGroup: animals.filter((a) => !a.Group && !a.BreedingGroup).length,
		};
		
		barnGroups.forEach((group) => {
			counts[GROUP_PREFIX + group] = animals.filter((a) => a.Group === group).length;
		});
		
		breedingGroups.forEach((group) => {
			counts[BREEDING_PREFIX + group] = animals.filter((a) => a.BreedingGroup === group).length;
		});
		
		return counts;
	}, [animals, barnGroups, breedingGroups]);

	const handleChange = (event) => {
		const newValue = event?.target?.value;
		if (newValue === undefined) {
			return;
		}

		if (controlledSelectedGroup === undefined) {
			setInternalSelectedGroup(newValue);
		}

		if (typeof onGroupChange === "function") {
			onGroupChange(newValue);
		} else {
			console.error("[GroupSelector] onGroupChange is not a function", { onGroupChange });
		}
	};

	// Get display name from value
	const getDisplayName = (value) => {
		if (!value || value === "all") return t("all_groups");
		if (value === "noGroup") return t("noGroup");
		if (value.startsWith(BREEDING_PREFIX)) {
			return cleanBreedingGroupName(value.slice(BREEDING_PREFIX.length));
		}
		if (value.startsWith(GROUP_PREFIX)) {
			return value.slice(GROUP_PREFIX.length);
		}
		return value;
	};

	// Check if value is a breeding group
	const isBreedingGroup = (value) => {
		return value && value.startsWith(BREEDING_PREFIX);
	};

	// Total number of groups (excluding "all")
	const totalGroupCount = barnGroups.length + breedingGroups.length + (hasNoGroupAnimals ? 1 : 0);

	return (
                <FormControl
                        size="small"
                        variant="outlined"
                        fullWidth={fullWidth}
                        sx={{
                                minWidth,
                                width: fullWidth ? "100%" : undefined,
                                flex: fullWidth ? 1 : undefined,
                                "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        ...(selectedGroup !== "all"
						? {
								borderColor: theme.palette.primary.main,
								backgroundColor: alpha(theme.palette.primary.main, 0.05),
								"& fieldset": {
									borderColor: theme.palette.primary.main,
								},
						  }
						: {
								"& fieldset": {
									borderColor: theme.palette.divider,
								},
						  }),
					"&:hover": {
						backgroundColor: alpha(theme.palette.primary.main, 0.04),
						"& fieldset": {
							borderColor: theme.palette.primary.main,
						},
					},
					"&.Mui-focused fieldset": {
						borderColor: theme.palette.primary.main,
					},
				},
                                "& .MuiInputLabel-root": {
                                        color: theme.palette.primary.main,
                                        backgroundColor: theme.palette.background.paper,
                                        px: 0.5,
                                        "&.Mui-focused": {
						color: textColor || theme.palette.primary.main,
					},
				},
			}}
		>
			<InputLabel id="group-select-label">{t("select-group")}</InputLabel>
			<Select
				labelId="group-select-label"
				value={selectedGroup}
				label={t("select-group")}
				onChange={handleChange}
				displayEmpty
				title={t("select-group")}
				renderValue={(value) => {
					if (!value || value === "all") {
						return (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: textColor }}>
								<FilterListIcon sx={{ fontSize: 20, color: textColor }} />
								{t("all_groups")}
							</Box>
						);
					}
					if (value === "noGroup") {
						return (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: textColor }}>
								<GroupIcon sx={{ fontSize: 20, opacity: 0.5, color: textColor }} />
								{t("noGroup")}
							</Box>
						);
					}
					// Breeding group
					if (isBreedingGroup(value)) {
						return (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: textColor }}>
								<PetsIcon sx={{ fontSize: 20 }} />
								{getDisplayName(value)}
								<Chip
									size="small"
									label={t("breeding-group-short") || "BG"}
									sx={{
										height: 18,
										fontSize: "0.65rem",
										backgroundColor: alpha(theme.palette.secondary.main, 0.2),
										color: textColor || theme.palette.secondary.main,
									}}
								/>
							</Box>
						);
					}
					// Barn group
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: textColor }}>
							<GroupIcon sx={{ fontSize: 20, color: textColor }} />
							{getDisplayName(value)}
						</Box>
					);
				}}
				startAdornment={
					selectedGroup !== "all" &&
					showCounts && (
						<Chip
							size="small"
							label={totalGroupCount}
							sx={{
								height: 20,
								fontSize: "0.75rem",
								backgroundColor: alpha(theme.palette.primary.main, 0.2),
								color: theme.palette.primary.main,
								mr: 0.5,
							}}
						/>
					)
				}
			>
				{/* All Groups Option */}
				<MenuItem
					value="all"
					sx={{
						"&:hover": {
							backgroundColor: alpha(theme.palette.primary.main, 0.08),
						},
						...(selectedGroup === "all" && {
							backgroundColor: alpha(theme.palette.primary.main, 0.12),
							"&:hover": {
								backgroundColor: alpha(theme.palette.primary.main, 0.16),
							},
						}),
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
						<FilterListIcon sx={{ fontSize: 20 }} />
						{t("all_groups")}
						{showCounts && (
							<Chip
								size="small"
								label={groupCounts.all}
								sx={{ ml: "auto", height: 20, fontSize: "0.75rem" }}
							/>
						)}
					</Box>
				</MenuItem>

				{/* Barn Groups Section */}
				{barnGroups.length > 0 && (
					<ListSubheader
						sx={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.text.secondary,
							fontSize: "0.75rem",
							lineHeight: "32px",
							fontWeight: 600,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<GroupIcon sx={{ fontSize: 16 }} />
							{t("barn-group") || "Barn Groups"}
						</Box>
					</ListSubheader>
				)}
				{barnGroups.map((group) => {
					const value = GROUP_PREFIX + group;
					return (
						<MenuItem
							key={value}
							value={value}
							sx={{
								pl: 3,
								"&:hover": {
									backgroundColor: alpha(theme.palette.primary.main, 0.08),
								},
								...(value === selectedGroup && {
									backgroundColor: alpha(theme.palette.primary.main, 0.12),
									"&:hover": {
										backgroundColor: alpha(theme.palette.primary.main, 0.16),
									},
								}),
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
								<GroupIcon sx={{ fontSize: 20 }} />
								{group}
								{suggestedGroups.includes(group) && (
									<Chip
										size="small"
										icon={<LightbulbIcon sx={{ fontSize: 14 }} />}
										label={t("suggested") || "Suggested"}
										sx={{
											ml: "auto",
											mr: showCounts ? 0.5 : 0,
											height: 20,
											fontSize: "0.7rem",
											backgroundColor: alpha(theme.palette.warning.main, 0.2),
											color: theme.palette.warning.dark,
											"& .MuiChip-icon": {
												color: theme.palette.warning.dark,
											},
										}}
									/>
								)}
								{showCounts && (
									<Chip
										size="small"
										label={groupCounts[value]}
										sx={{
											ml: suggestedGroups.includes(group) ? 0 : "auto",
											height: 20,
											fontSize: "0.75rem",
										}}
									/>
								)}
							</Box>
						</MenuItem>
					);
				})}

				{/* Divider between barn and breeding groups */}
				{barnGroups.length > 0 && breedingGroups.length > 0 && (
					<Divider sx={{ my: 0.5 }} />
				)}

				{/* Breeding Groups Section */}
				{breedingGroups.length > 0 && (
					<ListSubheader
						sx={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.secondary.main,
							fontSize: "0.75rem",
							lineHeight: "32px",
							fontWeight: 600,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<PetsIcon sx={{ fontSize: 16 }} />
							{t("breeding-group") || "Breeding Groups"}
						</Box>
					</ListSubheader>
				)}
				{breedingGroups.map((group) => {
					const value = BREEDING_PREFIX + group;
					const displayName = cleanBreedingGroupName(group);
					return (
						<MenuItem
							key={value}
							value={value}
							sx={{
								pl: 3,
								"&:hover": {
									backgroundColor: alpha(theme.palette.secondary.main, 0.08),
								},
								...(value === selectedGroup && {
									backgroundColor: alpha(theme.palette.secondary.main, 0.12),
									"&:hover": {
										backgroundColor: alpha(theme.palette.secondary.main, 0.16),
									},
								}),
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
								<PetsIcon sx={{ fontSize: 20 }} />
								{displayName}
								{suggestedGroups.includes(group) && (
									<Chip
										size="small"
										icon={<LightbulbIcon sx={{ fontSize: 14 }} />}
										label={t("suggested") || "Suggested"}
										sx={{
											ml: "auto",
											mr: showCounts ? 0.5 : 0,
											height: 20,
											fontSize: "0.7rem",
											backgroundColor: alpha(theme.palette.warning.main, 0.2),
											color: theme.palette.warning.dark,
											"& .MuiChip-icon": {
												color: theme.palette.warning.dark,
											},
										}}
									/>
								)}
								{showCounts && (
									<Chip
										size="small"
										label={groupCounts[value]}
										sx={{
											ml: suggestedGroups.includes(group) ? 0 : "auto",
											height: 20,
											fontSize: "0.75rem",
											backgroundColor: alpha(theme.palette.secondary.main, 0.1),
										}}
									/>
								)}
							</Box>
						</MenuItem>
					);
				})}

				{/* No Group Option */}
				{hasNoGroupAnimals && (barnGroups.length > 0 || breedingGroups.length > 0) && (
					<Divider sx={{ my: 0.5 }} />
				)}
				{hasNoGroupAnimals && (
					<MenuItem
						value="noGroup"
						sx={{
							"&:hover": {
								backgroundColor: alpha(theme.palette.primary.main, 0.08),
							},
							...(selectedGroup === "noGroup" && {
								backgroundColor: alpha(theme.palette.primary.main, 0.12),
								"&:hover": {
									backgroundColor: alpha(theme.palette.primary.main, 0.16),
								},
							}),
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
							<GroupIcon sx={{ fontSize: 20, opacity: 0.5 }} />
							{t("noGroup")}
							{showCounts && (
								<Chip
									size="small"
									label={groupCounts.noGroup}
									sx={{ ml: "auto", height: 20, fontSize: "0.75rem" }}
								/>
							)}
						</Box>
					</MenuItem>
				)}
			</Select>
		</FormControl>
	);
}
